/**
 * Pure pieces of the Reports page: template statistics per channel, the
 * comm-log results (table columns, statuses, why a comm failed, the report's
 * summary, the report file), the comm-log request, and the Comms Sent day.
 */
import {
    isoAddDays,
    istDateTime,
    istDateTimeEpoch,
    istDayEnd,
    istDayStart,
    istFileDateTime,
    istIsoDate,
} from '../utils/datetime'
import { toCSV } from '../utils/csv'

/** Every channel a template can have, in display order. */
export const REPORT_CHANNELS = [
    'email',
    'whatsapp',
    'sms',
    'voice',
    // Push and Slack were missing, so the per-channel rows did not add up to
    // the total (6 templates, rows summing to 4 on dev).
    'push_notification',
    'slack',
]

const countBy = (list) => ({
    total: list.length,
    approved: list.filter((t) => t.status === 'approved').length,
    pending: list.filter((t) => t.status === 'approval_awaited').length,
    disapproved: list.filter((t) => t.status === 'disapproved').length,
})

/**
 * Template counts for templates created in the last `days` days, overall and
 * per channel (every channel in REPORT_CHANNELS, even with zero).
 */
export const templateStats = (templates = [], nowSeconds, days) => {
    const cutoff = nowSeconds - days * 86400
    const inRange = templates.filter(
        (t) => t.created_on && t.created_on >= cutoff
    )
    return {
        ...countBy(inRange),
        byChannel: REPORT_CHANNELS.map((key) => ({
            key,
            ...countBy(inRange.filter((t) => t.comm_type === key)),
        })),
    }
}

// ── One comm log row ────────────────────────────────────────────

export const EXECUTION_STATUS = {
    SUCCESS: 'success',
    PENDING: 'pending',
    FAILURE: 'failure',
}

/**
 * The overall state of one comm, as the old comms app's "Execution Status"
 * column worked it out: success when it was registered and sent; pending
 * when it was registered and the send has not failed (not sent yet);
 * failure otherwise (registration failed, or the send failed).
 *
 * @param {object} row  a comm log row from /comms/v2/generate_comm_reports
 * @returns {'success'|'pending'|'failure'}
 */
export const executionStatus = (row) => {
    const registered = row?.registration_status === 'success'
    if (registered && row?.execution_status === 'success')
        return EXECUTION_STATUS.SUCCESS
    if (registered && row?.execution_status !== 'failed')
        return EXECUTION_STATUS.PENDING
    return EXECUTION_STATUS.FAILURE
}

/**
 * The latest delivery update from the channel's provider, or null.
 *
 * The server keeps only the last entry of `external_status_log` in a report
 * row (reports_generation_service `__add_pipeline_name`); a list is still
 * read to its last entry in case a row comes through untrimmed.
 */
const latestLogEntry = (row) => {
    const log = row?.external_status_log
    if (Array.isArray(log)) return log.length ? log[log.length - 1] : null
    return log && typeof log === 'object' ? log : null
}

/** The provider's latest status for the comm ("delivered", "failed", ...), or ''. */
export const latestStatus = (row) => latestLogEntry(row)?.status || ''

/** When the provider's latest status arrived (epoch seconds), or null. */
export const latestStatusTime = (row) => latestLogEntry(row)?.time ?? null

/**
 * The provider's errors on the latest status, each with code, title,
 * message, details (`error_data.details`) and link (`href`), as the old
 * app's Error Info dialog listed them. Empty when there are none.
 */
export const errorEntries = (row) => {
    const errors = latestLogEntry(row)?.errors
    if (!Array.isArray(errors)) return []
    return errors.map((e) => ({
        code: e?.code ?? '',
        title: e?.title ?? '',
        message: e?.message ?? '',
        details: e?.error_data?.details ?? '',
        link: e?.href ?? '',
    }))
}

/** Words for the stage a comm failed at, in the Error Info dialog and the summary. */
export const STAGE_LABEL = {
    registration: 'Registering',
    send: 'Sending',
    delivery: 'Delivery',
}

/** The reason shown for a failure the server kept no details of. */
export const NO_REASON = 'The provider gave no details.'

/**
 * Why the comm failed, or null for one that has not.
 *
 * The server builds `failure` ({stage, code, message, reason}) from the
 * stored error records (reports_generation_service `__prepare_report_rows`,
 * since 26 Oct 2026): the stage that failed, the provider's code and message,
 * and a plain-words reason for the codes it knows. A row served before that
 * carries none, so the provider's delivery errors stand in, and a failed
 * comm with nothing stored at all still counts as failed, with NO_REASON.
 *
 * @param {object} row  a comm log row from /comms/v2/generate_comm_reports
 * @returns {{stage: string, code: string, message: string, reason: string}|null}
 */
export const failureOf = (row) => {
    const served = row?.failure
    if (
        served &&
        typeof served === 'object' &&
        (served.reason || served.message)
    )
        return {
            stage: served.stage || '',
            code: served.code ?? '',
            message: served.message || '',
            reason: served.reason || served.message,
        }
    // The stages in the order a comm passes through them, as the server
    // reads them: a failed registration never reached a sender, a refused
    // send was never delivered, a delivery failure is a status on a send
    // that succeeded. A row served before the field has no details for the
    // first two.
    const noDetails = (stage) => ({
        stage,
        code: '',
        message: '',
        reason: NO_REASON,
    })
    if (row?.registration_status === 'failed') return noDetails('registration')
    if (row?.execution_status === 'failed') return noDetails('send')
    const [first] = errorEntries(row)
    if (first) {
        // Title, message and details, each once: Meta's title and message
        // differ, AWS repeats the message as the details.
        const message = [first.title, first.message, first.details]
            .filter((part, i, all) => part && all.indexOf(part) === i)
            .join(': ')
        return { stage: 'delivery', code: first.code, message, reason: message }
    }
    if (latestStatus(row) === 'failed') return noDetails('delivery')
    return null
}

/** The plain-words reason a comm failed, or '' for one that has not. */
export const failureReason = (row) => failureOf(row)?.reason ?? ''

/** True when the Error Info dialog has something to say: the comm failed somewhere. */
export const hasErrorInfo = (row) => failureOf(row) !== null

/**
 * The failure as one line of text, for the report file: the code, the reason
 * and the provider's message when it differs from the reason.
 */
const errorText = (row) => {
    const failure = failureOf(row)
    if (!failure) return ''
    const providerMessage =
        failure.message && failure.message !== failure.reason
            ? failure.message
            : ''
    return [failure.code, failure.reason, providerMessage]
        .filter(Boolean)
        .join(': ')
}

/**
 * The outcome of a whole report, for its summary: how many comms succeeded,
 * are pending or failed, and the failure reasons with how many comms each hit,
 * most common first. A comm the provider accepted and then could not deliver
 * counts as failed: to the reader it did not arrive.
 *
 * @param {object[]} rows
 * @returns {{total: number, success: number, pending: number, failure: number,
 *     reasons: {reason: string, count: number}[]}}
 */
export const reportSummary = (rows = []) => {
    const summary = { total: rows.length, success: 0, pending: 0, failure: 0 }
    const byReason = new Map()
    rows.forEach((row) => {
        const failure = failureOf(row)
        if (failure) {
            summary.failure += 1
            byReason.set(
                failure.reason,
                (byReason.get(failure.reason) || 0) + 1
            )
        } else if (executionStatus(row) === EXECUTION_STATUS.SUCCESS) {
            summary.success += 1
        } else {
            summary.pending += 1
        }
    })
    return {
        ...summary,
        reasons: [...byReason]
            .map(([reason, count]) => ({ reason, count }))
            .sort((a, b) => b.count - a.count),
    }
}

/**
 * The columns of the report table: the old comms app's thirteen, in its
 * order, plus Reason after Execution Status (why the comm failed, in words).
 * `value(row)` is the text a cell shows; Error Info is a button on the page.
 */
export const REPORT_COLUMNS = [
    { key: 'event_type', label: 'Event Type', value: (r) => r.event_type },
    {
        key: 'comm_type',
        label: 'Communication Type',
        value: (r) => r.comm_type,
    },
    {
        key: 'scheduled_delivery_time',
        label: 'Scheduled Delivery Time',
        value: (r) => istDateTime(r.scheduled_delivery_time),
    },
    {
        key: 'delivered_time',
        label: 'Delivered Time',
        value: (r) => istDateTime(latestStatusTime(r)),
    },
    {
        key: 'latest_status',
        label: 'Latest Status',
        value: (r) => latestStatus(r) || '-',
    },
    {
        key: 'execution_status',
        label: 'Execution Status',
        value: executionStatus,
    },
    { key: 'reason', label: 'Reason', value: failureReason },
    { key: 'error_info', label: 'Error Info', value: errorText },
    { key: 'receiver_id', label: 'Lead ID', value: (r) => r.receiver_id },
    {
        key: 'receiver_identity_email',
        label: 'Recipient Email',
        value: (r) => r.receiver_identity_email,
    },
    {
        key: 'receiver_identity_mobile_no',
        label: 'Recipient Mobile Number',
        value: (r) => r.receiver_identity_mobile_no,
    },
    {
        key: 'receiver_type',
        label: 'Receiver Type',
        value: (r) => r.receiver_type,
    },
    {
        key: 'pipeline_name',
        label: 'Pipeline Name',
        value: (r) => r.pipeline_name,
    },
    {
        key: 'template_name',
        label: 'Template Name',
        value: (r) => r.template_name,
    },
]

/**
 * The report file's columns: the server's column list for a large report
 * (reports_generation_service `process_multipart_upload`), in its order,
 * with readable headers, India-time dates in the server's format, the status
 * log flattened into its status, time and errors, and the Execution Status
 * the page shows. The stored execution_status keeps its own column ("Send
 * Result"), so nothing the server returns is lost.
 */
export const REPORT_FILE_COLUMNS = [
    {
        label: 'Scheduled Delivery Time',
        value: (r) => istFileDateTime(r.scheduled_delivery_time),
    },
    { label: 'Execution Status', value: executionStatus },
    { label: 'Send Result', value: (r) => r.execution_status },
    {
        label: 'Provider Response Code',
        value: (r) => r.external_api_status_code,
    },
    { label: 'Pipeline ID', value: (r) => r.pipeline_id },
    {
        label: 'Recipient Mobile Number',
        value: (r) => r.receiver_identity_mobile_no,
    },
    { label: 'Receiver Type', value: (r) => r.receiver_type },
    { label: 'Latest Status', value: latestStatus },
    {
        label: 'Delivered Time',
        value: (r) => istFileDateTime(latestStatusTime(r)),
    },
    { label: 'Error Info', value: errorText },
    { label: 'Reason', value: failureReason },
    {
        label: 'Registration Time',
        value: (r) => istFileDateTime(r.registration_event_time),
    },
    { label: 'Template Name', value: (r) => r.template_name },
    { label: 'Event Type', value: (r) => r.event_type },
    { label: 'Template ID', value: (r) => r.template_id },
    { label: 'Execution ID', value: (r) => r.execution_id },
    { label: 'Lead ID', value: (r) => r.receiver_id },
    { label: 'Communication Type', value: (r) => r.comm_type },
    { label: 'Registration Status', value: (r) => r.registration_status },
    { label: 'Recipient Email', value: (r) => r.receiver_identity_email },
    { label: 'Pipeline Name', value: (r) => r.pipeline_name },
    {
        label: 'Execution Time',
        value: (r) => istFileDateTime(r.execution_event_time),
    },
]

/**
 * The comm-log rows as a CSV file's text: one line per row through
 * REPORT_FILE_COLUMNS, '' for no rows.
 *
 * @param {object[]} rows
 * @returns {string}
 */
export const reportCsv = (rows = []) => {
    const labels = REPORT_FILE_COLUMNS.map((c) => c.label)
    const records = rows.map((row) =>
        Object.fromEntries(
            REPORT_FILE_COLUMNS.map((c) => [c.label, c.value(row) ?? ''])
        )
    )
    return toCSV(records, labels)
}

// ── The comm-log request ────────────────────────────────────────

/** An empty filter form for the comm-log search. */
export const emptyReportFilters = () => ({
    reportId: '',
    leadId: '',
    email: '',
    mobileNo: '',
    eventType: '',
    start: '',
    end: '',
})

/**
 * The filters the server can look comm logs up by. A date range only
 * narrows one of these: the server answers 400 "Invalid query args" without
 * one (reports_generation_service `__build_key_condition_expression`).
 */
const IDENTITY_FILTERS = [
    'reportId',
    'leadId',
    'email',
    'mobileNo',
    'eventType',
]

/**
 * The first reason the comm-log request cannot be sent, or '' when it can.
 *
 * @param {object} filters  see emptyReportFilters; start and end are
 *     datetime-local values in India time
 * @returns {string}
 */
export const reportRequestProblem = (filters) => {
    if (!IDENTITY_FILTERS.some((k) => (filters[k] || '').trim()))
        return 'Enter a Report ID, Lead ID, Email, Mobile No or Event Type. A date range alone cannot be searched.'
    const start = istDateTimeEpoch(filters.start)
    const end = istDateTimeEpoch(filters.end)
    if (filters.start && start === null) return 'Enter a valid start.'
    if (filters.end && end === null) return 'Enter a valid end.'
    if (start !== null && end !== null && start > end)
        return 'The start must be before the end.'
    return ''
}

/**
 * The body of POST /comms/v2/generate_comm_reports for these filters.
 *
 * Text filters are trimmed and left out when empty. The range is read as
 * India time; the end covers its whole minute (an end of 10:30 includes a
 * comm sent at 10:30:40).
 *
 * @param {object} filters  see emptyReportFilters
 * @param {string} commId   the report job's id, which the file is polled by
 */
export const reportPayload = (filters, commId) => {
    const text = (v) => (v || '').trim()
    const start = istDateTimeEpoch(filters.start)
    const end = istDateTimeEpoch(filters.end)
    return {
        comm_id: commId,
        ...(text(filters.reportId) && {
            registration_event_id: text(filters.reportId),
        }),
        ...(text(filters.leadId) && { receiver_id: text(filters.leadId) }),
        ...(text(filters.email) && { email: text(filters.email) }),
        ...(text(filters.mobileNo) && { mobile_no: text(filters.mobileNo) }),
        ...(text(filters.eventType) && { event_type: text(filters.eventType) }),
        ...(start !== null && { start_time: start }),
        ...(end !== null && { end_time: end + 59 }),
    }
}

// ── Comms Sent ─────────────────────────────────────────────────

/**
 * The quick choices of the Comms Sent card. Only Yesterday: counting a week
 * or a month reads every comm log of every channel in the range and timed
 * out, so those choices went (26 Oct 2026) and the server refuses a range
 * longer than a day. Any other day is picked as one date.
 */
export const VOLUME_PRESETS = [{ key: 'yesterday', label: 'Yesterday' }]

/**
 * How far back a range may start, in whole days before today: CommLogs rows
 * expire after 180 days, and the server refuses a start older than that
 * (reports_generation_service `get_comm_volume_report`).
 */
export const VOLUME_MAX_DAYS_BACK = 179

/**
 * The epoch range of one whole India-time day: its first to its last second.
 *
 * @param {string} isoDate  'YYYY-MM-DD'
 * @returns {{start: number, end: number, date: string}}
 */
export const volumeDayRange = (isoDate) => ({
    start: istDayStart(isoDate),
    end: istDayEnd(isoDate),
    date: isoDate,
})

/**
 * The range of a Comms Sent preset: yesterday, the whole India-time day
 * before today. Any other key is read as yesterday.
 *
 * @param {'yesterday'} key
 * @param {number} nowSeconds
 * @returns {{start: number, end: number, date: string}}
 */
export const volumePresetRange = (key, nowSeconds) =>
    volumeDayRange(isoAddDays(istIsoDate(nowSeconds), -1))

/**
 * The first reason a chosen Comms Sent date cannot be counted, or ''.
 *
 * @param {string} isoDate    'YYYY-MM-DD'
 * @param {number} nowSeconds
 */
export const volumeDateProblem = (isoDate, nowSeconds) => {
    if (!isoDate) return 'Pick a date.'
    const today = istIsoDate(nowSeconds)
    if (isoDate < isoAddDays(today, -VOLUME_MAX_DAYS_BACK))
        return `Comms are kept for 180 days: pick a date on or after ${isoAddDays(
            today,
            -VOLUME_MAX_DAYS_BACK
        )}.`
    if (isoDate > today) return 'The date cannot be after today.'
    return ''
}

/** The number of comms across every channel row. */
export const volumeTotal = (rows = []) =>
    rows.reduce((sum, r) => sum + (Number(r?.count) || 0), 0)
